import { useState, useEffect } from 'react';
import { Shield, Users, LogOut, Monitor, MapPin, Calendar, RefreshCw } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import {
  getMySessions,
  logoutMySession,
  getAllActiveSessions,
  forceLogoutSession,
  getAllLoginHistory
} from '../../../actions/session-manager';
import { toastMessage } from '../../../utils/toast-message';
import Loader from '../../../components/common/Loader/Loader';
import { ADMIN_ROLES } from '../../../constants/roles';

interface ActiveSession {
  session_uuid: string;
  user_uuid: string;
  user_name: string;
  user_email: string;
  ip_address: string;
  device_info: string;
  login_method: 'email' | 'facebook' | 'google' | 'whatsapp';
  login_at: string;
  last_activity_at: string;
}

interface MySession {
  session_uuid: string;
  ip_address: string;
  device_info: string;
  user_agent?: string;
  login_method: 'email' | 'facebook' | 'google' | 'whatsapp';
  login_at: string;
  last_activity_at: string;
  is_current?: boolean;
}

interface LoginHistoryRecord {
  history_uuid: string;
  user_uuid: string;
  user_name?: string;
  user_email?: string;
  ip_address: string;
  device_info: string;
  user_agent: string;
  login_method: 'email' | 'facebook' | 'google' | 'whatsapp';
  login_status: 'success' | 'failed';
  failure_reason?: string;
  login_at: string;
  logout_at?: string;
  logout_type?: 'manual' | 'auto' | 'forced';
  session_duration?: number;
}

// Utility function to get user data
const getUserFromLocalStorage = (): any | null => {
  if (typeof window === 'undefined') return null;

  // Try new format first (crm_user)
  const crmUserStr = localStorage.getItem('crm_user');
  if (crmUserStr) {
    try {
      const crmUser = JSON.parse(crmUserStr);
      return crmUser.data || crmUser;
    } catch (error) {
      console.error('Error parsing crm_user data from localStorage:', error);
    }
  }

  // Fallback to old format (user)
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    return null;
  }
};

const SessionManagementPage = () => {
  const user = getUserFromLocalStorage();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'my-sessions' | 'sessions' | 'history'>('my-sessions');
  const [mySessions, setMySessions] = useState<MySession[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [loginHistory, setLoginHistory] = useState<LoginHistoryRecord[]>([]);
  const [processingLogout, setProcessingLogout] = useState<string | null>(null);

  // Filters for history
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [filterMethod, setFilterMethod] = useState<string>('all');

  // Check if user is super admin based on role UUID
  const roleUuid = user?.role?.uuid || user?.role_uuid;
  const isSuperAdmin = ADMIN_ROLES.includes(roleUuid);

  useEffect(() => {
    // All users can access "My Sessions" tab
    if (activeTab === 'my-sessions') {
      fetchMySessions();
    }
    // Only super admins can access other tabs
    else if (isSuperAdmin) {
      if (activeTab === 'sessions') {
        fetchActiveSessions();
      } else if (activeTab === 'history') {
        fetchLoginHistory();
      }
    }
  }, [activeTab, isSuperAdmin]);

  useEffect(() => {
    if (activeTab === 'history' && isSuperAdmin) {
      fetchLoginHistory();
    }
  }, [filterStatus, filterMethod]);

  const fetchMySessions = async () => {
    try {
      setLoading(true);
      const response = await getMySessions();

      if (response.success && response.data?.sessions) {
        setMySessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching my sessions:', error);
      toastMessage('Failed to load your sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      setLoading(true);
      const response = await getAllActiveSessions();

      if (response.success && response.data?.sessions) {
        setActiveSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      toastMessage('Failed to load active sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };

      if (filterStatus !== 'all') {
        params.login_status = filterStatus;
      }

      if (filterMethod !== 'all') {
        params.login_method = filterMethod;
      }

      const response = await getAllLoginHistory(params);

      if (response.success && response.data?.history) {
        setLoginHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
      toastMessage('Failed to load login history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutMySession = async (sessionUuid: string, deviceInfo: any) => {
    const deviceName = `${deviceInfo.device} - ${deviceInfo.browser}`;
    if (!confirm(`Are you sure you want to logout from ${deviceName}? You will need to log in again on that device.`)) {
      return;
    }

    try {
      setProcessingLogout(sessionUuid);
      const response = await logoutMySession(sessionUuid);

      if (response.success) {
        toastMessage(`Successfully logged out from ${deviceName}`, 'success');
        await fetchMySessions();
      } else {
        toastMessage(response.message || 'Failed to logout session', 'error');
      }
    } catch (error) {
      console.error('Error logging out session:', error);
      toastMessage('Failed to logout session', 'error');
    } finally {
      setProcessingLogout(null);
    }
  };

  const handleForceLogout = async (sessionUuid: string, userName: string) => {
    if (!confirm(`Are you sure you want to force logout ${userName}? They will be immediately logged out.`)) {
      return;
    }

    try {
      setProcessingLogout(sessionUuid);
      const response = await forceLogoutSession(sessionUuid);

      if (response.success) {
        toastMessage(`Successfully logged out ${userName}`, 'success');
        await fetchActiveSessions();
      } else {
        toastMessage(response.message || 'Failed to force logout', 'error');
      }
    } catch (error) {
      console.error('Error forcing logout:', error);
      toastMessage('Failed to force logout session', 'error');
    } finally {
      setProcessingLogout(null);
    }
  };

  const parseDeviceInfo = (deviceInfoStr: string) => {
    try {
      return JSON.parse(deviceInfoStr);
    } catch {
      return { device: 'Unknown', browser: 'Unknown', platform: 'Unknown' };
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  };

  const getTimeSinceActivity = (lastActivity: string) => {
    const now = new Date().getTime();
    const last = new Date(lastActivity).getTime();
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getLoginMethodBadge = (method: string) => {
    const colors = {
      email: 'bg-brandColor/10 text-brandColor dark:bg-brandColor/20 dark:text-brandColor',
      facebook: 'bg-brandColor/10 text-brandColor dark:bg-brandColor/20 dark:text-brandColor',
      google: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      whatsapp: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
    return colors[method as keyof typeof colors] || colors.email;
  };

  const getStatusBadge = (status: string) => {
    if (status === 'success') {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  const getLogoutTypeBadge = (type?: string) => {
    const colors = {
      manual: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      auto: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      forced: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    };
    return colors[type as keyof typeof colors] || colors.manual;
  };

  if (loading && mySessions.length === 0 && activeSessions.length === 0 && loginHistory.length === 0) {
    return (
      <div className="w-full">
        <ModernSettingHeader
          title="Session Management"
          description="Manage user sessions and login activity"
          icon={<Shield className="h-5 w-5" />}
        />
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <Loader size={42} text="Loading session data..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Session Management"
        description="Manage user sessions and login activity across the platform"
        icon={<Shield className="h-5 w-5" />}
      />

      <div className="p-6">
        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('my-sessions')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'my-sessions'
                  ? 'bg-brandColor text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Monitor className="w-4 h-4" />
              My Sessions ({mySessions.length})
            </button>
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('sessions')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'sessions'
                      ? 'bg-brandColor text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  All Sessions ({activeSessions.length})
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'history'
                      ? 'bg-brandColor text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Login History
                </button>
              </>
            )}
          </div>
        </div>

      {/* My Sessions Tab */}
      {activeTab === 'my-sessions' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              My Active Sessions
            </h3>
            <button
              onClick={fetchMySessions}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {mySessions.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Monitor className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active sessions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Login Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Device & Browser
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Login Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {mySessions.map((session) => {
                    const deviceInfo = parseDeviceInfo(session.device_info);
                    return (
                      <tr key={session.session_uuid} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${session.is_current ? 'bg-brandColor/10 dark:bg-brandColor/20' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLoginMethodBadge(session.login_method)}`}>
                            {session.login_method}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start text-sm">
                            <Monitor className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {deviceInfo.device} - {deviceInfo.browser}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400 text-xs">
                                {deviceInfo.platform}
                              </div>
                              {session.is_current && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 mt-1">
                                  Current Session
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                            {session.ip_address || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {new Date(session.login_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="text-gray-900 dark:text-white">
                              {getTimeSinceActivity(session.last_activity_at)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(session.last_activity_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {session.is_current ? (
                            <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                              Current session
                            </span>
                          ) : (
                            <button
                              onClick={() => handleLogoutMySession(session.session_uuid, deviceInfo)}
                              disabled={processingLogout === session.session_uuid}
                              className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <LogOut className="w-4 h-4 mr-1" />
                              {processingLogout === session.session_uuid ? 'Logging out...' : 'Logout'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-brandColor/10 dark:bg-brandColor/20 border-t border-brandColor/30 dark:border-brandColor/40 p-4">
            <div className="flex items-start">
              <Shield className="w-5 h-5 text-brandColor dark:text-brandColor mr-3 mt-0.5" />
              <div className="text-sm text-brandColor dark:text-brandColor">
                <p className="font-medium mb-1">Manage Your Active Sessions</p>
                <ul className="space-y-1 text-xs">
                  <li>• You can login on up to 3 devices simultaneously</li>
                  <li>• Click "Logout" to remotely sign out from other devices</li>
                  <li>• Sessions automatically expire after 24 hours of inactivity</li>
                  <li>• Your current session is highlighted and cannot be logged out here</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Sessions Tab */}
      {activeTab === 'sessions' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Active User Sessions
            </h3>
            <button
              onClick={fetchActiveSessions}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {activeSessions.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active sessions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Login Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Device & Browser
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Login Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {activeSessions.map((session) => {
                    const deviceInfo = parseDeviceInfo(session.device_info);
                    return (
                      <tr key={session.session_uuid} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {session.user_name}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {session.user_email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLoginMethodBadge(session.login_method)}`}>
                            {session.login_method}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start text-sm">
                            <Monitor className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {deviceInfo.device} - {deviceInfo.browser}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400 text-xs">
                                {deviceInfo.platform}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                            {session.ip_address || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {new Date(session.login_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="text-gray-900 dark:text-white">
                              {getTimeSinceActivity(session.last_activity_at)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(session.last_activity_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleForceLogout(session.session_uuid, session.user_name)}
                            disabled={processingLogout === session.session_uuid}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <LogOut className="w-4 h-4 mr-1" />
                            {processingLogout === session.session_uuid ? 'Logging out...' : 'Force Logout'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Login History Tab */}
      {activeTab === 'history' && (
        <>
          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <div className="flex gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === 'all'
                        ? 'bg-brandColor text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilterStatus('success')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === 'success'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Success
                  </button>
                  <button
                    onClick={() => setFilterStatus('failed')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === 'failed'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Failed
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Login Method
                </label>
                <select
                  value={filterMethod}
                  onChange={(e) => setFilterMethod(e.target.value)}
                  className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Methods</option>
                  <option value="email">Email</option>
                  <option value="facebook">Facebook</option>
                  <option value="google">Google</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
            </div>
          </div>

          {/* History Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Login History
              </h3>
              <button
                onClick={fetchLoginHistory}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>

            {loginHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No login history found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Device & Browser
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Session Info
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loginHistory.map((record) => {
                      const deviceInfo = parseDeviceInfo(record.device_info);
                      return (
                        <tr key={record.history_uuid} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {record.user_name || 'Unknown'}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400">
                                {record.user_email || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {new Date(record.login_at).toLocaleDateString()}
                                </div>
                                <div className="text-gray-500 dark:text-gray-400">
                                  {new Date(record.login_at).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLoginMethodBadge(record.login_method)}`}>
                              {record.login_method}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-start text-sm">
                              <Monitor className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {deviceInfo.device} - {deviceInfo.browser}
                                </div>
                                <div className="text-gray-500 dark:text-gray-400 text-xs">
                                  {deviceInfo.platform}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900 dark:text-white">
                              <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                              {record.ip_address || 'Unknown'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(record.login_status)}`}>
                              {record.login_status}
                            </span>
                            {record.failure_reason && (
                              <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                {record.failure_reason}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {record.logout_at ? (
                              <div className="text-sm">
                                <div className="flex items-center text-gray-900 dark:text-white">
                                  <LogOut className="w-4 h-4 mr-2 text-gray-400" />
                                  <span>Duration: {formatDuration(record.session_duration)}</span>
                                </div>
                                {record.logout_type && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${getLogoutTypeBadge(record.logout_type)}`}>
                                    {record.logout_type === 'manual' && 'Manual Logout'}
                                    {record.logout_type === 'auto' && 'Auto Expired'}
                                    {record.logout_type === 'forced' && 'Forced Logout'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Active</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

        {/* Security Notice */}
        <div className="bg-brandColor/10 dark:bg-brandColor/20 border border-brandColor/30 dark:border-brandColor/40 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-brandColor dark:text-brandColor mb-3 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Admin Security Notice
          </h3>
          <ul className="space-y-2 text-sm text-brandColor dark:text-brandColor">
            <li>• You can view all active user sessions and force logout when necessary</li>
            <li>• Force logout immediately terminates the user's session and requires them to log in again</li>
            <li>• All force logout actions are logged in the login history with "Forced Logout" status</li>
            <li>• Sessions automatically expire after 24 hours of inactivity</li>
            <li>• Login history is retained for 90 days and automatically cleaned up</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SessionManagementPage;
